import type { ReactNode } from "react";
import { vars } from "@pack-it-up/ui-web";
import { APP_NAME } from "../../constants.js";
import { LogoMarkIcon } from "../../app/icons.js";

// The brand lockup (amber suitcase mark + wordmark), shared with the sidebar, so the
// auth screens carry the same identity.
const Brand = (): ReactNode => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: vars.space.sm }}>
    <span
      style={{
        width: "32px",
        height: "32px",
        borderRadius: vars.radius.sm,
        background: vars.color.accent,
        color: vars.color.accentText,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      aria-hidden
    >
      <LogoMarkIcon size={20} />
    </span>
    <span style={{ fontFamily: vars.font.heading, fontWeight: vars.font.weightBold, fontSize: vars.font.sizeXl, letterSpacing: vars.font.trackingSnug }}>
      {APP_NAME}
    </span>
  </div>
);

// Minimal centered card shell shared by the auth screens. Inline styles reference the
// theme tokens (vanilla-extract vars are CSS custom-property references), so no extra
// `.css.ts` is needed for this small layout.
export const AuthCard = ({ title, children, footer }: { title: string; children: ReactNode; footer?: ReactNode }): ReactNode => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: vars.space.lg,
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: "380px",
        background: vars.color.surface,
        border: `1px solid ${vars.color.border}`,
        borderRadius: vars.radius.lg,
        padding: vars.space.xl,
        display: "flex",
        flexDirection: "column",
        gap: vars.space.lg,
      }}
    >
      <Brand />
      <h1 style={{ margin: 0, fontFamily: vars.font.heading, fontSize: vars.font.sizeXl, letterSpacing: "-0.4px" }}>
        {title}
      </h1>
      {children}
      {footer !== undefined && (
        <div style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted, display: "flex", gap: vars.space.sm, flexWrap: "wrap" }}>
          {footer}
        </div>
      )}
    </div>
  </div>
);

// Inline error banner (role=alert) for the store's mapped error message.
export const AuthError = ({ message }: { message: string }): ReactNode => (
  <div
    role="alert"
    style={{
      background: vars.color.surfaceSunken,
      color: vars.color.danger,
      border: `1px solid ${vars.color.danger}`,
      borderRadius: vars.radius.md,
      padding: `${vars.space.sm} ${vars.space.md}`,
      fontSize: vars.font.sizeSm,
    }}
  >
    {message}
  </div>
);
